/**
 * Documentation hub — in-app reader for role-scoped handbooks.
 *
 * - Sidebar lists the handbooks the current user can access (RBAC enforced server-side).
 * - Main pane renders the markdown body with "Modern / minimal" aesthetic.
 * - Header chips emphasise ISO Grade · Data Security · Reduce daily workload.
 * - "Download .docx" pulls the same content as a styled Word file.
 *
 * Mounted at:
 *   /app/docs           (tenant shell — visible to every tenant staff user)
 *   /admin/docs         (admin shell — super admin sees only the admin handbook)
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import { ShieldCheck, FileDown, Sparkles, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/time";

const TRUST_CHIPS = [
    { label: "ISO Grade", icon: ShieldCheck },
    { label: "Data Security", icon: ShieldCheck },
    { label: "Reduce Daily Workload", icon: Sparkles },
];

export default function Documentation() {
    const [docs, setDocs] = useState([]);
    const [activeSlug, setActiveSlug] = useState(null);
    const [activeDoc, setActiveDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [docLoading, setDocLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/docs").then(r => {
            if (!active) return;
            const list = r.data?.docs || [];
            setDocs(list);
            if (list[0]) setActiveSlug(list[0].slug);
            setLoading(false);
        }).catch(e => {
            if (!active) return;
            toast.error(apiError(e));
            setLoading(false);
        });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!activeSlug) { setActiveDoc(null); return; }
        let active = true;
        setDocLoading(true);
        api.get(`/docs/${activeSlug}`).then(r => {
            if (active) setActiveDoc(r.data);
        }).catch(e => {
            if (active) toast.error(apiError(e));
        }).finally(() => {
            if (active) setDocLoading(false);
        });
        return () => { active = false; };
    }, [activeSlug]);

    const download = async () => {
        if (!activeSlug) return;
        setDownloading(true);
        try {
            const res = await api.get(`/docs/${activeSlug}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `law-os-${activeSlug}.docx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Downloaded — open in Word / Google Docs");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="docs-page">
            {/* ---- Hero header ---- */}
            <header className="border border-border bg-card px-6 py-5 rounded-sm">
                <div className="flex items-center gap-2 text-primary text-[10px] uppercase tracking-[0.22em] mb-2">
                    <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Documentation
                </div>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                    Your enterprise handbook
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Single source of truth for running Law&nbsp;OS — written for your role,
                    available as a live web page and as a downloadable Word document.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                    {TRUST_CHIPS.map(({ label, icon: Icon }) => (
                        <span
                            key={label}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-primary/30 text-primary text-[10px] uppercase tracking-[0.18em] rounded-sm bg-primary/[0.04]"
                            data-testid={`docs-trust-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <Icon className="w-3 h-3" strokeWidth={1.5} />
                            {label}
                        </span>
                    ))}
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
                {/* ---- Sidebar list ---- */}
                <aside className="border border-border bg-card rounded-sm overflow-hidden" data-testid="docs-sidebar">
                    <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Handbooks for you
                    </div>
                    {loading && (
                        <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                        </div>
                    )}
                    {!loading && docs.length === 0 && (
                        <div className="px-4 py-6 text-xs text-muted-foreground" data-testid="docs-empty">
                            No handbooks available for your role.
                        </div>
                    )}
                    {!loading && docs.map(d => (
                        <button
                            key={d.slug}
                            onClick={() => setActiveSlug(d.slug)}
                            data-testid={`docs-item-${d.slug}`}
                            className={`w-full text-left px-4 py-3 border-b border-[#1F1F1F] last:border-b-0 transition-colors ${activeSlug === d.slug ? "bg-primary/[0.06]" : "hover:bg-muted"}`}
                        >
                            <div className={`text-sm font-medium ${activeSlug === d.slug ? "text-primary" : "text-foreground"}`}>
                                {d.title}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                                {d.audience_label}
                            </div>
                        </button>
                    ))}
                </aside>

                {/* ---- Main content ---- */}
                <section className="border border-border bg-card rounded-sm" data-testid="docs-content">
                    {docLoading && (
                        <div className="px-6 py-10 text-xs text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading document…
                        </div>
                    )}
                    {!docLoading && activeDoc && (
                        <article className="px-6 sm:px-10 py-8">
                            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-primary">
                                    {activeDoc.audience_label || "Handbook"}
                                </div>
                                <button
                                    onClick={download}
                                    disabled={downloading}
                                    data-testid="docs-download"
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors rounded-sm"
                                >
                                    {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" strokeWidth={1.5} />}
                                    {downloading ? "Preparing…" : "Download .docx"}
                                </button>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight" data-testid="docs-title">
                                {activeDoc.title}
                            </h2>
                            {activeDoc.subtitle && (
                                <p className="text-sm text-muted-foreground mt-2">
                                    {activeDoc.subtitle}
                                </p>
                            )}
                            {activeDoc.updated_at && (
                                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-3">
                                    Last updated · {new Date(activeDoc.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}
                                </div>
                            )}
                            <div className="mt-6 prose-invert max-w-none" data-testid="docs-body">
                                {renderMarkdown(activeDoc.body_md)}
                            </div>
                        </article>
                    )}
                    {!docLoading && !activeDoc && !loading && (
                        <div className="px-6 py-10 text-xs text-muted-foreground">
                            Pick a handbook from the left to view it here.
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
